import * as $bool from "../gleam_stdlib/gleam/bool.mjs";
import * as $dict from "../gleam_stdlib/gleam/dict.mjs";
import * as $float from "../gleam_stdlib/gleam/float.mjs";
import * as $int from "../gleam_stdlib/gleam/int.mjs";
import * as $list from "../gleam_stdlib/gleam/list.mjs";
import * as $option from "../gleam_stdlib/gleam/option.mjs";
import * as $result from "../gleam_stdlib/gleam/result.mjs";
import * as $yielder from "../gleam_yielder/gleam/yielder.mjs";
import * as $p5js_gleam from "../p5js_gleam/p5js_gleam.mjs";
import * as $priorityq from "../priorityq/priorityq.mjs";
import * as $random from "../prng/prng/random.mjs";
import {
  Ok,
  Error,
  toList,
  prepend as listPrepend,
  CustomType as $CustomType,
  makeError,
  divideFloat,
  isEqual,
} from "./gleam.mjs";
import * as $obstacle from "./obstacle.mjs";
import * as $pit from "./pit.mjs";
import * as $room from "./room.mjs";
import * as $vector from "./vector.mjs";

const FILEPATH = "src/dungeon.gleam";

export class Dungeon extends $CustomType {
  constructor(rooms, pits, obstacles) {
    super();
    this.rooms = rooms;
    this.pits = pits;
    this.obstacles = obstacles;
  }
}
export const Dungeon$Dungeon = (rooms, pits, obstacles) =>
  new Dungeon(rooms, pits, obstacles);
export const Dungeon$isDungeon = (value) => value instanceof Dungeon;
export const Dungeon$Dungeon$rooms = (value) => value.rooms;
export const Dungeon$Dungeon$0 = (value) => value.rooms;
export const Dungeon$Dungeon$pits = (value) => value.pits;
export const Dungeon$Dungeon$1 = (value) => value.pits;
export const Dungeon$Dungeon$obstacles = (value) => value.obstacles;
export const Dungeon$Dungeon$2 = (value) => value.obstacles;

const dungeon_size = 7;

const max_depth = dungeon_size;

const room_rate = 0.8;

const break_rate = 0.3;

/**
 * The size of each room in pixels.
 */
export const room_size = 100;

const pit_count = 5;

const pit_size = 20.0;

const minimum_pit_distance = 80.0;

const obstacle_rate = 0.5;

const obstacle_limit = 3;

/**
 * Gets the indices of the room that would be in the given direction from the given indices.
 */
export function next_room_indices(column, row, direction) {
  if (direction instanceof $room.Left) {
    return [column - 1, row];
  } else if (direction instanceof $room.Right) {
    return [column + 1, row];
  } else if (direction instanceof $room.Top) {
    return [column, row - 1];
  } else if (direction instanceof $room.Bottom) {
    return [column, row + 1];
  } else if (direction instanceof $room.TopLeft) {
    return [column - 1, row - 1];
  } else if (direction instanceof $room.TopRight) {
    return [column + 1, row - 1];
  } else if (direction instanceof $room.BottomLeft) {
    return [column - 1, row + 1];
  } else {
    return [column + 1, row + 1];
  }
}

function coordinate_direction(from, to) {
  let fc = from[0];
  let fr = from[1];
  let tc = to[0];
  let tr = to[1];
  if ((fc === tc) && (fr < tr)) {
    return new Ok(new $room.Bottom());
  } else {
    let fc = from[0];
    let fr = from[1];
    let tc = to[0];
    let tr = to[1];
    if ((fc < tc) && (fr < tr)) {
      return new Ok(new $room.BottomRight());
    } else {
      let fc = from[0];
      let fr = from[1];
      let tc = to[0];
      let tr = to[1];
      if ((fc === tc) && (fr > tr)) {
        return new Ok(new $room.Top());
      } else {
        let fc = from[0];
        let fr = from[1];
        let tc = to[0];
        let tr = to[1];
        if ((fc > tc) && (fr > tr)) {
          return new Ok(new $room.TopLeft());
        } else {
          let fc = from[0];
          let fr = from[1];
          let tc = to[0];
          let tr = to[1];
          if ((fc < tc) && (fr === tr)) {
            return new Ok(new $room.Right());
          } else {
            let fc = from[0];
            let fr = from[1];
            let tc = to[0];
            let tr = to[1];
            if ((fc < tc) && (fr > tr)) {
              return new Ok(new $room.TopRight());
            } else {
              let fc = from[0];
              let fr = from[1];
              let tc = to[0];
              let tr = to[1];
              if ((fc > tc) && (fr === tr)) {
                return new Ok(new $room.Left());
              } else {
                let fc = from[0];
                let fr = from[1];
                let tc = to[0];
                let tr = to[1];
                if ((fc > tc) && (fr < tr)) {
                  return new Ok(new $room.BottomLeft());
                } else {
                  return new Error(undefined);
                }
              }
            }
          }
        }
      }
    }
  }
}

/**
 * Checks if a point is on top of a pit.
 */
export function is_over_pit(dungeon, position) {
  return $list.any(
    dungeon.pits,
    (pit) => { return $vector.distance(pit.position, position) < pit.size; },
  );
}

function center() {
  return globalThis.Math.trunc(dungeon_size / 2);
}

function coordinate_out_of_bounds(column, row) {
  return (((column < 0) || (column >= dungeon_size)) || (row < 0)) || (row >= dungeon_size);
}

function compute_corner_walls(rooms) {
  return $dict.fold(
    rooms,
    rooms,
    (rooms, _use1, r) => {
      let column;
      let row;
      column = _use1[0];
      row = _use1[1];
      let open_corner = (room, dir1, dir2, out_dir) => {
        let can_go_in_dir = $room.is_navigable(room, dir1) && $room.is_navigable(
          room,
          dir2,
        );
        return $bool.guard(
          !can_go_in_dir,
          room,
          () => {
            let $ = next_room_indices(column, row, out_dir);
            let next_column;
            let next_row;
            next_column = $[0];
            next_row = $[1];
            return $bool.guard(
              coordinate_out_of_bounds(next_column, next_row),
              room,
              () => {
                let next_room = $dict.get(rooms, [next_column, next_row]);
                if (next_room instanceof Ok) {
                  let next_room$1 = next_room[0];
                  let can_go_in_dir$1 = $room.is_navigable(
                    next_room$1,
                    $room.inverse_direction(dir1),
                  ) && $room.is_navigable(
                    next_room$1,
                    $room.inverse_direction(dir2),
                  );
                  if (can_go_in_dir$1) {
                    return $room.set_navigable(room, out_dir, true);
                  } else {
                    return room;
                  }
                } else {
                  return room;
                }
              },
            );
          },
        );
      };
      let _block;
      let _pipe = r;
      let _pipe$1 = open_corner(
        _pipe,
        new $room.Right(),
        new $room.Bottom(),
        new $room.BottomRight(),
      );
      let _pipe$2 = open_corner(
        _pipe$1,
        new $room.Left(),
        new $room.Bottom(),
        new $room.BottomLeft(),
      );
      let _pipe$3 = open_corner(
        _pipe$2,
        new $room.Right(),
        new $room.Top(),
        new $room.TopRight(),
      );
      _block = open_corner(
        _pipe$3,
        new $room.Left(),
        new $room.Top(),
        new $room.TopLeft(),
      );
      let r$1 = _block;
      return $dict.insert(rooms, [column, row], r$1);
    },
  );
}

function generate_rooms(rooms, column, row, recursion_depth, previous_direction) {
  let r = $room.initialize_unbounded_room();
  let _block;
  if (recursion_depth === 0) {
    _block = r;
  } else {
    _block = $room.set_navigable(r, previous_direction, true);
  }
  let r$1 = _block;
  let rooms$1 = $dict.insert(rooms, [column, row], r$1);
  return $bool.guard(
    recursion_depth > max_depth,
    rooms$1,
    () => {
      let advance_direction = (rooms, direction) => {
        return $bool.guard(
          (isEqual(direction, previous_direction)) && (recursion_depth !== 0),
          rooms,
          () => {
            let $ = next_room_indices(column, row, direction);
            let next_column;
            let next_row;
            next_column = $[0];
            next_row = $[1];
            return $bool.guard(
              coordinate_out_of_bounds(next_column, next_row),
              rooms,
              () => {
                return $bool.guard(
                  $dict.has_key(rooms, [next_column, next_row]),
                  rooms,
                  () => {
                    let _block$1;
                    let _pipe = $random.float(0.0, 1.0);
                    _block$1 = $random.random_sample(_pipe);
                    let room_roll = _block$1;
                    return $bool.guard(
                      room_roll > room_rate,
                      rooms,
                      () => {
                        let $1 = $dict.get(rooms, [column, row]);
                        let r$2;
                        if ($1 instanceof Ok) {
                          r$2 = $1[0];
                        } else {
                          throw makeError(
                            "let_assert",
                            FILEPATH,
                            "dungeon",
                            122,
                            "generate_rooms",
                            "Pattern match failed, no pattern matched the value.",
                            {
                              value: $1,
                              start: 3091,
                              end: 3141,
                              pattern_start: 3102,
                              pattern_end: 3107
                            }
                          )
                        }
                        let r$3 = $room.set_navigable(r$2, direction, true);
                        let rooms$1 = $dict.insert(rooms, [column, row], r$3);
                        return generate_rooms(
                          rooms$1,
                          next_column,
                          next_row,
                          recursion_depth + 1,
                          $room.inverse_direction(direction),
                        );
                      },
                    );
                  },
                );
              },
            );
          },
        );
      };
      let _pipe = rooms$1;
      let _pipe$1 = advance_direction(_pipe, new $room.Left());
      let _pipe$2 = advance_direction(_pipe$1, new $room.Right());
      let _pipe$3 = advance_direction(_pipe$2, new $room.Top());
      return advance_direction(_pipe$3, new $room.Bottom());
    },
  );
}

function break_walls(rooms) {
  return $dict.fold(
    rooms,
    rooms,
    (rooms, _use1, _) => {
      let column;
      let row;
      column = _use1[0];
      row = _use1[1];
      let break_in_direction = (rooms, dir) => {
        let $ = next_room_indices(column, row, dir);
        let next_column;
        let next_row;
        next_column = $[0];
        next_row = $[1];
        return $bool.guard(
          coordinate_out_of_bounds(next_column, next_row),
          rooms,
          () => {
            let next_room = $dict.get(rooms, [next_column, next_row]);
            if (next_room instanceof Ok) {
              let next_room$1 = next_room[0];
              let _block;
              let _pipe = $random.float(0.0, 1.0);
              _block = $random.random_sample(_pipe);
              let break_roll = _block;
              return $bool.guard(
                break_roll > break_rate,
                rooms,
                () => {
                  let $1 = $dict.get(rooms, [column, row]);
                  let room;
                  if ($1 instanceof Ok) {
                    room = $1[0];
                  } else {
                    throw makeError(
                      "let_assert",
                      FILEPATH,
                      "dungeon",
                      194,
                      "break_walls",
                      "Pattern match failed, no pattern matched the value.",
                      {
                        value: $1,
                        start: 5388,
                        end: 5441,
                        pattern_start: 5399,
                        pattern_end: 5407
                      }
                    )
                  }
                  let room$1 = $room.set_navigable(room, dir, true);
                  let rooms$1 = $dict.insert(rooms, [column, row], room$1);
                  let next_room$2 = $room.set_navigable(
                    next_room$1,
                    $room.inverse_direction(dir),
                    true,
                  );
                  return $dict.insert(
                    rooms$1,
                    [next_column, next_row],
                    next_room$2,
                  );
                },
              );
            } else {
              return rooms;
            }
          },
        );
      };
      let _pipe = rooms;
      let _pipe$1 = break_in_direction(_pipe, new $room.Right());
      return break_in_direction(_pipe$1, new $room.Bottom());
    },
  );
}

/**
 * The total size of the dungeon in pixels.
 */
export function total_size() {
  return $int.to_float(dungeon_size * room_size);
}

function point_out_of_bounds(position) {
  let size = total_size();
  return (((position.x < 0.0) || (position.x >= size)) || (position.y < 0.0)) || (position.y >= size);
}

/**
 * Given a coordinate find the center point of the room.
 */
export function coordinate_to_point(coordinate) {
  let x = $int.to_float(coordinate[0] * room_size) + ($int.to_float(room_size) / 2.0);
  let y = $int.to_float(coordinate[1] * room_size) + ($int.to_float(room_size) / 2.0);
  return new $vector.Vector(x, y, 0.0);
}

function get_random_point_in_room(loop$rooms) {
  while (true) {
    let rooms = loop$rooms;
    let _block;
    let _pipe = $random.int(0, dungeon_size);
    _block = $random.random_sample(_pipe);
    let random_x = _block;
    let _block$1;
    let _pipe$1 = $random.int(0, dungeon_size);
    _block$1 = $random.random_sample(_pipe$1);
    let random_y = _block$1;
    let coordinate = [random_x, random_y];
    let $ = $dict.get(rooms, coordinate);
    if ($ instanceof Ok) {
      return coordinate_to_point(coordinate);
    } else {
      loop$rooms = rooms;
    }
  }
}

/**
 * Renders the dungeon to the screen.
 */
export function draw(p, dungeon) {
  $yielder.each(
    $yielder.range(0, dungeon_size),
    (col) => {
      return $yielder.each(
        $yielder.range(0, dungeon_size),
        (row) => {
          return $result.map(
            $dict.get(dungeon.rooms, [col, row]),
            (r) => { return $room.draw(p, r, col, row, room_size); },
          );
        },
      );
    },
  );
  $list.each(dungeon.pits, (pit) => { return $pit.draw(p, pit); });
  return $list.each(
    dungeon.obstacles,
    (obstacle) => { return $obstacle.draw(p, obstacle); },
  );
}

/**
 * Get the coordinate that the given point is in.
 */
export function point_to_coordinate(point) {
  let column = $float.truncate(divideFloat(point.x, $int.to_float(room_size)));
  let row = $float.truncate(divideFloat(point.y, $int.to_float(room_size)));
  return [column, row];
}

/**
 * Checks if it is possible to move from one position to another.
 */
export function can_move(dungeon, from, to) {
  return $bool.guard(
    point_out_of_bounds(to),
    false,
    () => {
      let from_coordinate = point_to_coordinate(from);
      let to_coordinate = point_to_coordinate(to);
      let from_room = $dict.get(dungeon.rooms, from_coordinate);
      let to_room = $dict.get(dungeon.rooms, to_coordinate);
      if (from_room instanceof Ok && to_room instanceof Ok) {
        let from$1 = from_room[0];
        let dir = coordinate_direction(from_coordinate, to_coordinate);
        if (dir instanceof Ok) {
          let dir$1 = dir[0];
          return $room.is_navigable(from$1, dir$1);
        } else {
          return true;
        }
      } else {
        return false;
      }
    },
  );
}

/**
 * Checks if going from 1 point to another hits a wall. If it does then return the direction away from the wall.
 */
export function get_reflecting_point(dungeon, from, to) {
  return $bool.guard(
    point_out_of_bounds(to),
    new Error(undefined),
    () => {
      let from_coordinate = point_to_coordinate(from);
      let to_coordinate = point_to_coordinate(to);
      let from_room = $dict.get(dungeon.rooms, from_coordinate);
      let to_room = $dict.get(dungeon.rooms, to_coordinate);
      if (from_room instanceof Ok && to_room instanceof Ok) {
        let from$1 = from_room[0];
        return $result.try$(
          coordinate_direction(from_coordinate, to_coordinate),
          (dir) => {
            return $bool.guard(
              $room.is_navigable(from$1, dir),
              new Error(undefined),
              () => { return new Ok($room.inverse_direction(dir)); },
            );
          },
        );
      } else {
        return new Error(undefined);
      }
    },
  );
}

function path_from_costs(seen_so_far, end) {
  let $ = $dict.get(seen_so_far, end);
  let prev;
  if ($ instanceof Ok) {
    prev = $[0][2];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "dungeon",
      589,
      "path_from_costs",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $,
        start: 17813,
        end: 17870,
        pattern_start: 17824,
        pattern_end: 17841
      }
    )
  }
  if (prev instanceof $option.Some) {
    let prev$1 = prev[0];
    return listPrepend(
      coordinate_to_point(end),
      path_from_costs(seen_so_far, prev$1),
    );
  } else {
    return toList([]);
  }
}

function path_to_helper(dungeon, to, worklist, seen_so_far) {
  return $bool.guard(
    $priorityq.is_empty(worklist),
    toList([]),
    () => {
      let $ = $priorityq.peek(worklist);
      let t;
      let cost;
      if ($ instanceof $option.Some) {
        t = $[0][0];
        cost = $[0][1];
      } else {
        throw makeError(
          "let_assert",
          FILEPATH,
          "dungeon",
          532,
          "path_to_helper",
          "Pattern match failed, no pattern matched the value.",
          {
            value: $,
            start: 16007,
            end: 16071,
            pattern_start: 16018,
            pattern_end: 16044
          }
        )
      }
      let worklist$1 = $priorityq.pop(worklist);
      let $1 = isEqual(t, to);
      if ($1) {
        return $list.reverse(path_from_costs(seen_so_far, to));
      } else {
        let _block;
        let _pipe = toList([
          new $room.Left(),
          new $room.Right(),
          new $room.Top(),
          new $room.Bottom(),
          new $room.TopLeft(),
          new $room.TopRight(),
          new $room.BottomLeft(),
          new $room.BottomRight(),
        ]);
        _block = $list.fold(
          _pipe,
          [worklist$1, seen_so_far],
          (acc, dir) => {
            let worklist$2;
            let seen_so_far$1;
            worklist$2 = acc[0];
            seen_so_far$1 = acc[1];
            let $3 = $dict.get(dungeon.rooms, t);
            let to_room;
            if ($3 instanceof Ok) {
              to_room = $3[0];
            } else {
              throw makeError(
                "let_assert",
                FILEPATH,
                "dungeon",
                551,
                "path_to_helper",
                "Pattern match failed, no pattern matched the value.",
                {
                  value: $3,
                  start: 16569,
                  end: 16620,
                  pattern_start: 16580,
                  pattern_end: 16591
                }
              )
            }
            let $4 = $room.is_navigable(to_room, dir);
            if ($4) {
              let next = next_room_indices(t[0], t[1], dir);
              let next_cost = cost + $vector.distance(
                new $vector.Vector(
                  $int.to_float(next[0]),
                  $int.to_float(next[1]),
                  0.0,
                ),
                new $vector.Vector(
                  $int.to_float(to[0]),
                  $int.to_float(to[1]),
                  0.0,
                ),
              );
              let $5 = $dict.get(seen_so_far$1, next);
              if ($5 instanceof Ok) {
                let old_best = $5[0][1];
                if (old_best < next_cost) {
                  return [worklist$2, seen_so_far$1];
                } else {
                  return [
                    (() => {
                      let _pipe$1 = worklist$2;
                      return $priorityq.push(
                        _pipe$1,
                        [next, next_cost, new $option.Some(t)],
                      );
                    })(),
                    (() => {
                      let _pipe$1 = seen_so_far$1;
                      return $dict.insert(
                        _pipe$1,
                        next,
                        [next, next_cost, new $option.Some(t)],
                      );
                    })(),
                  ];
                }
              } else {
                return [
                  (() => {
                    let _pipe$1 = worklist$2;
                    return $priorityq.push(
                      _pipe$1,
                      [next, next_cost, new $option.Some(t)],
                    );
                  })(),
                  (() => {
                    let _pipe$1 = seen_so_far$1;
                    return $dict.insert(
                      _pipe$1,
                      next,
                      [next, next_cost, new $option.Some(t)],
                    );
                  })(),
                ];
              }
            } else {
              return [worklist$2, seen_so_far$1];
            }
          },
        );
        let $2 = _block;
        let worklist$2;
        let seen_so_far$1;
        worklist$2 = $2[0];
        seen_so_far$1 = $2[1];
        return path_to_helper(dungeon, to, worklist$2, seen_so_far$1);
      }
    },
  );
}

/**
 * Gets a path from 1 point to another in a dungeon
 */
export function path_to(dungeon, from, to) {
  let start = point_to_coordinate(from);
  let initial_entry = [start, 0.0, new $option.None()];
  return path_to_helper(
    dungeon,
    point_to_coordinate(to),
    (() => {
      let _pipe = $priorityq.new$(
        (a, b) => { return $float.compare(b[1], a[1]); },
      );
      return $priorityq.push(_pipe, initial_entry);
    })(),
    $dict.from_list(toList([[start, initial_entry]])),
  );
}

function too_close_to_existing_pit(pits, position) {
  return $list.any(
    pits,
    (pit) => {
      let distance = $vector.distance(pit.position, position);
      return distance < minimum_pit_distance;
    },
  );
}

function attempt_place_obstacle(point, pits, tries) {
  return $bool.guard(
    tries > 3,
    new Error(undefined),
    () => {
      let max_offset = ($int.to_float(room_size) - $obstacle.size) / 2.0;
      let _block;
      let _pipe = $random.float(max_offset * -1.0, max_offset);
      _block = $random.random_sample(_pipe);
      let offset_x = _block;
      let _block$1;
      let _pipe$1 = $random.float(max_offset * -1.0, max_offset);
      _block$1 = $random.random_sample(_pipe$1);
      let offset_y = _block$1;
      let position = $vector.add(
        point,
        new $vector.Vector(offset_x, offset_y, 0.0),
      );
      let $ = too_close_to_existing_pit(pits, position);
      if ($) {
        return attempt_place_obstacle(point, pits, tries + 1);
      } else {
        return new Ok(new $obstacle.Obstacle(position));
      }
    },
  );
}

function too_close_to_player_spawn(position) {
  let c = center();
  return $vector.distance(coordinate_to_point([c, c]), position) < minimum_pit_distance;
}

/**
 * Get a random location to place an object.
 * Objects should not be too close to existing pits and should not be too close to where the player starts.
 */
export function get_location_to_place_object(loop$rooms, loop$pits) {
  while (true) {
    let rooms = loop$rooms;
    let pits = loop$pits;
    let point = get_random_point_in_room(rooms);
    let _block;
    let _pipe = $random.float(-30.0, 30.0);
    _block = $random.random_sample(_pipe);
    let offset_x = _block;
    let _block$1;
    let _pipe$1 = $random.float(-30.0, 30.0);
    _block$1 = $random.random_sample(_pipe$1);
    let offset_y = _block$1;
    let position = $vector.add(
      point,
      new $vector.Vector(offset_x, offset_y, 0.0),
    );
    let $ = too_close_to_existing_pit(pits, position) || too_close_to_player_spawn(
      position,
    );
    if ($) {
      loop$rooms = rooms;
      loop$pits = pits;
    } else {
      return position;
    }
  }
}

function generate_pits(rooms) {
  let _pipe = $list.range(1, pit_count);
  return $list.fold(
    _pipe,
    toList([]),
    (pits, _) => {
      let position = get_location_to_place_object(rooms, pits);
      return listPrepend(new $pit.Pit(position, pit_size), pits);
    },
  );
}

function generate_obstacles(rooms, pits) {
  return $dict.fold(
    rooms,
    toList([]),
    (obstacles, coordinate, _) => {
      let _block;
      let _pipe = $random.float(0.0, 1.0);
      _block = $random.random_sample(_pipe);
      let obstacle_roll = _block;
      return $bool.guard(
        obstacle_roll > obstacle_rate,
        obstacles,
        () => {
          let point = coordinate_to_point(coordinate);
          let _block$1;
          let _pipe$1 = $random.int(0, obstacle_limit);
          _block$1 = $random.random_sample(_pipe$1);
          let obstacle_count = _block$1;
          return $list.fold(
            $list.range(1, obstacle_count),
            obstacles,
            (obstacles, _) => {
              let $ = attempt_place_obstacle(point, pits, 0);
              if ($ instanceof Ok) {
                let obstacle = $[0];
                return listPrepend(obstacle, obstacles);
              } else {
                return obstacles;
              }
            },
          );
        },
      );
    },
  );
}

/**
 * Creates the initial game dungeon.
 */
export function generate_dungeon() {
  let center$1 = center();
  let _block;
  let _pipe = $dict.new$();
  let _pipe$1 = generate_rooms(_pipe, center$1, center$1, 0, new $room.Left());
  let _pipe$2 = break_walls(_pipe$1);
  _block = compute_corner_walls(_pipe$2);
  let rooms = _block;
  let pits = generate_pits(rooms);
  let obstacles = generate_obstacles(rooms, pits);
  return new Dungeon(rooms, pits, obstacles);
}
